import React from "react";
import Layout from "../components/layout";
import { PageTitle } from "../components/Common/PageTitle";
import { JoinNewsLetterButton } from "../components/Common/NewsLetterPlug";
import { Link } from "gatsby";
import { SalesTag } from "../components/Blog/Tag";
import { inPersonClasses } from "../content/inPersonClasses";
import { Photo } from "../components/Photo";
import studio from "../static/in-person-classes/studio.png";
import studiowebp from "../static/in-person-classes/studio.webp";
import { LanguageToggle } from "../components/Common/LanguageToggle";

// Text content in both languages
const content = {
  en: {
    title: "Welcome to my wax carving studio!",
    subtitle: "",
    metaDescription:
      "Join our in-person wax carving classes in our new studio space. Flexible summer schedule available for personalized learning experience.",
    pageTitle: "Wax Carvers - In-Person Classes",
    studioAlt: "Image of the wax carving studio",
    intro1:
      "Join me in my studio in Arnhem and learn to wax carve your own jewellery pieces. Bring your creativity and carve your dream pieces to life! Take a look at the different classes available and find something that fits your interests and skill level.",
    intro2:
      "Wait! This sounds interesting but I'm not 100% sure what wax carving is exactly…",
    intro3:
      "Read about wax carving and the <a href='/lost-wax-casting' className='text-blue-600 hover:text-blue-800 underline'>lost wax casting process</a> first. Then come back here to book your wax carving class!",
    comingSoon: "Coming soon",
  },
  nl: {
    title: "Welkom in mijn wax carving atelier!",
    subtitle: "",
    metaDescription:
      "Doe mee aan onze persoonlijke wax carving lessen in onze nieuwe studio ruimte. Flexibele zomerschema beschikbaar voor gepersonaliseerde leerervaring.",
    pageTitle: "Wax Carvers - Persoonlijke Lessen",
    studioAlt: "Afbeelding van de wax carving studio",
    intro1:
      "Kom naar mijn atelier in Arnhem en leer je eigen sieraden te maken door middel van wax carving. Neem je creativiteit mee en breng je droom sieraad tot leven! Bekijk de verschillende lessen en kies iets dat past bij je interesse en niveau.\n",
    intro2:
      "Dit klinkt interessant maar ik weet eigenlijk niet precies wat wax carving is…",
    intro3:
      "Lees hier eerst meer over wax caving en het <a href='/lost-wax-casting' className='text-blue-600 hover:text-blue-800 underline'>verloren was proces</a>.  Kom daarna terug om je wax carving les te boeken!",
    comingSoon: "Binnenkort",
  },
};

// Component to render HTML content safely
const HTMLContent = ({ content, className = "" }) => {
  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

const ClassCard = ({
  image,
  title,
  description,
  timeframe,
  disabled,
  tooltip,
  tags = [],
  onSelectTag,
  salesPageUrl,
  identifier,
  t,
}) => (
  <div
    className="space-y-4 bg-gray-50 shadow-lg rounded-lg"
    data-tip={disabled ? tooltip : undefined}
  >
    <div className="aspect-w-6 aspect-h-5">
      <img
        className={`object-cover rounded-t-lg`}
        src={image}
        alt={title}
      />
    </div>

    <div className="space-y-2">
      <div className="px-4 sm:px-6 text-lg leading-6 font-medium space-y-1">
        <h3 className="text-gray-900">{title}</h3>
      </div>
      <div className="px-4 pt-1 pb-5 sm:px-6">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 mb-5">
          <div className="sm:col-span-2">
            <dd className="mt-1  prose prose-indigo text-gray-700 mb-8 h-24">
              {description}
            </dd>
          </div>
        </dl>
        <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
          <div className="max-w-screen-xl mx-auto my-auto text-center">
            <div className="flex justify-center align-middle">
              {!disabled ? (
                <div className="inline-flex rounded-md shadow">
                  <Link
                    to={salesPageUrl ?? `/class/${identifier}`}
                    className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-white bg-blue-700 hover:bg-blue-500 focus:outline-none focus:ring transition duration-150 ease-in-out"
                  >
                    More information
                  </Link>
                </div>
              ) : (
                <div className="inline-flex rounded-md">
                  <span className="inline-flex items-center justify-center px-5 py-3 text-base leading-6 font-medium rounded-md text-blue-700">
                    {t.comingSoon} {timeframe}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-6 flex items-center">
          <div className="ml-3">
            {tags.map((it) => (
              <div
                key={it}
                className="cursor-pointer"
                onClick={() => onSelectTag(it)}
              >
                <SalesTag tag={it} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const InPersonClasses = () => {
  const [lang, setLang] = React.useState("nl");
  const t = content[lang];

  const classes = inPersonClasses
    .filter((it) => !it.unlisted)
    .filter((it) => !it.freebie)
    .map((it) => ({
      ...it,
      description:
        lang === "nl" ? (it.description_nl ?? it.description) : it.description,
      disabled: !it.salesPageReleased,
    }));

  return (
    <Layout
      description={t.metaDescription}
      title={t.pageTitle}
    >
      <div className="relative pb-6 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0">
          <div className="bg-white h-1/3 sm:h-2/3"></div>
        </div>
        <div className="max-w-7xl mx-auto relative">
          <LanguageToggle lang={lang} setLang={setLang} />
          <PageTitle title={t.title} subtitle={t.subtitle} />
          <Photo
            collage={studio}
            collagewp={studiowebp}
            altText={t.studioAlt}
            small
          />
          <div className="mt-12 prose prose-indigo mx-auto text-gray-700">
            <p>
              <HTMLContent content={t.intro1} />
            </p>

            <p>
              <HTMLContent content={t.intro2} />
            </p>

            <p>
              <HTMLContent content={t.intro3} />
            </p>
          </div>

          <div className="max-w-7xl mx-auto relative">
            {/*<Tags selected={selected} classes={classes} onSelect={setSelected} />*/}
            <div className="mt-12 max-w-lg mx-auto grid gap-5 lg:grid-cols-3 lg:max-w-none">
              {classes.map((it) => (
                <ClassCard {...it} key={it.timeframe + it.title} t={t} />
              ))}
            </div>
          </div>
        </div>
        {/* Studio Address Information */}
        <div className="my-8 p-6 bg-blue-50 border-l-4 border-blue-400 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            {lang === 'en' ? 'Studio Location' : 'Atelier Locatie'}
          </h3>
          <div className="text-gray-700">
            <p className="mb-2">
              <strong>{lang === 'en' ? 'Address:' : 'Adres:'}</strong><br />
              Wax Carvers
              <br />
              Oude Kraan 72 (OK72)
              <br />
              6811 LL Arnhem

            </p>
            <p className="text-sm text-gray-600">
              {lang === 'en'
                ? 'Use the intercom next to the door to call Wax Carvers. Go through 2 sets of sliding doors and take the lift to the 5th floor. I’m located in room 512.'
                : 'Gebruik de intercom naast de deur om Wax Carvers te bellen. Ga door 2 schuifdeuren en neem de lift naar de 5e verdieping. Ik zit in kamer 512.'}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <JoinNewsLetterButton />
      </div>
    </Layout>
  );
};

export default InPersonClasses;
